export type TimestampLike = {
  toDate?: () => Date;
  seconds?: number;
  _seconds?: number;
  nanoseconds?: number;
};

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const isValidDate = (value: Date): boolean => !Number.isNaN(value.getTime());

export const parseDateLike = (value: unknown): Date | null => {
  if (!value) return null;

  if (value instanceof Date) {
    return isValidDate(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) return null;

    const dateOnlyMatch = trimmedValue.match(DATE_ONLY_RE);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0);
    }

    const parsedDate = new Date(trimmedValue);
    return isValidDate(parsedDate) ? parsedDate : null;
  }

  if (typeof value === 'number') {
    const parsedDate = new Date(value);
    return isValidDate(parsedDate) ? parsedDate : null;
  }

  if (typeof value === 'object') {
    const timestampLike = value as TimestampLike;

    if (typeof timestampLike.toDate === 'function') {
      const parsedDate = timestampLike.toDate();
      return isValidDate(parsedDate) ? parsedDate : null;
    }

    if (typeof timestampLike.seconds === 'number') {
      const parsedDate = new Date(timestampLike.seconds * 1000);
      return isValidDate(parsedDate) ? parsedDate : null;
    }

    if (typeof timestampLike._seconds === 'number') {
      const parsedDate = new Date(timestampLike._seconds * 1000);
      return isValidDate(parsedDate) ? parsedDate : null;
    }
  }

  return null;
};

const toTwoDigits = (value: number): string => value.toString().padStart(2, '0');

export const toDateKey = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (DATE_ONLY_RE.test(trimmedValue)) {
      return trimmedValue;
    }
  }

  const parsedDate = parseDateLike(value);
  if (!parsedDate) return null;

  return `${parsedDate.getFullYear()}-${toTwoDigits(parsedDate.getMonth() + 1)}-${toTwoDigits(parsedDate.getDate())}`;
};
